import {
  useReorderLeaseCompImagesMutation,
  useUploadLeaseCompImageMutation,
} from "@/services/media";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { DndContext } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { EmeraldFileUpload } from "@emerald-react/file-upload";
import { EmeraldNotification } from "@emerald-react/notification";
import { useSurvey } from "@hooks/useSurveySelectors";
import { useSortableList } from "@shared/hooks/useSortable";
import type { LeaseCompMediaFile } from "@survey-types/survey.leaseComp.types";
import {
  addLeaseCompImage,
  removeLeaseCompImage,
  reorderLeaseCompImages,
} from "@survey/leaseCompsSlice";
import React, { useState } from "react";
import LeaseCompPhotoThumbnail from "./LeaseCompPhotoThumbnail";

interface LeaseCompPhotoUploadProps {
  ownerId: string;
}

const LeaseCompPhotoUpload = ({ ownerId }: LeaseCompPhotoUploadProps) => {
  const [errorBool, setError] = useState(false);
  const [failedFiles, setFailedFiles] = useState<
    Array<{ fileName: string; errorMsg: string }>
  >([]);

  const [uploadLeaseCompImage] = useUploadLeaseCompImageMutation();
  const [reorderLeaseCompImageAPI] = useReorderLeaseCompImagesMutation();

  const currentSurvey = useSurvey();
  const dispatch = useAppDispatch();
  const useremail = localStorage.getItem("email");

  // Read media directly from the lease comps slice; this state is hydrated earlier
  // in the lease comp details flow and should not be overwritten here.
  const mediaFiles = useAppSelector((state): LeaseCompMediaFile[] => {
    const leaseCompMedia = state.leaseComps.mediaFiles?.[ownerId];

    if (Array.isArray(leaseCompMedia)) {
      return leaseCompMedia;
    }

    if (Array.isArray(leaseCompMedia?.images)) {
      return leaseCompMedia.images;
    }

    return [];
  });

  const validImageTypes = [
    "image/gif",
    "image/jpeg",
    "image/png",
    "image/jpg",
    "image/avif",
    "image/av1",
  ];

  const acceptedKeyTypes = validImageTypes
    .map((type) => type.replace("image/", "").toLocaleUpperCase())
    .join("/");

  const hideProcessingNodes = () => {
    document
      .querySelectorAll<HTMLElement>('[class="MuiGrid-root MuiGrid-item"]')
      .forEach((node) => {
        if (node.outerHTML.includes("Processing...")) {
          node.hidden = true;
        }
      });
  };

  const setUploadError = (msg: string, fileName?: string) => {
    setError(true);
    setFailedFiles((prev) => [
      ...prev,
      { fileName: fileName ?? "Unknown file", errorMsg: msg },
    ]);
    hideProcessingNodes();
  };

  const checkForValidImage = (file: File): boolean => {
    if (
      !file.name.toLowerCase().match(/\.(png|jpeg|gif|jpg|avif|av1)$/) ||
      !validImageTypes.includes(file.type)
    ) {
      setUploadError(
        "Only GIF/JPEG/PNG/AVIF/AV1 image formats are supported.",
        file.name,
      );
      return false;
    }
    return true;
  };

  const handleFileUpload = (
    _event:
      | React.ChangeEvent<HTMLInputElement>
      | React.DragEvent<HTMLDivElement>,
    files: File[],
  ) => {
    setError(false);
    setFailedFiles([]);
    files.forEach(async (file) => {
      if (!checkForValidImage(file)) return;

      const payloadMedia = {
        surveyId: currentSurvey?.id,
        user: useremail,
        propertyId: ownerId,
        mediaType: "IMAGES",
        ownerType: "LEASE_COMP",
      };

      const formData = new FormData();
      formData.append(
        "file",
        file,
        file.name.replace(/[^a-zA-Z0-9.\-_ ]/g, "_"),
      );
      formData.append("media", JSON.stringify(payloadMedia));

      // Optimistic update
      const tempMediaId = "temp-id-" + crypto.randomUUID();
      const tempMediaFile: LeaseCompMediaFile = {
        mediaId: tempMediaId,
        leaseCompId: ownerId,
        displayName: file.name,
        mediaType: "IMAGES",
        s3Url: "",
        thumbnailUrl: "",
        cloudfrontUrl: "",
        isHidden: false,
        surveyId: currentSurvey?.id || "",
        order: (mediaFiles.length || 0).toString(),
        status: "UPLOADING",
        hashCode: "",
        keyName: "",
        userId: useremail || "",
      };

      dispatch(
        addLeaseCompImage({
          leaseCompId: ownerId,
          mediaFile: tempMediaFile,
        }),
      );

      uploadLeaseCompImage(formData)
        .unwrap()
        .then((result) => {
          // Replace temp placeholder with the real uploaded file
          dispatch(
            removeLeaseCompImage({
              leaseCompId: ownerId,
              mediaId: tempMediaId,
            }),
          );
          dispatch(
            addLeaseCompImage({
              leaseCompId: ownerId,
              mediaFile: { ...result, leaseCompId: ownerId },
            }),
          );
        })
        .catch((error) => {
          setUploadError(
            "File upload failed. " + (error.data?.error || ""),
            file.name,
          );
          dispatch(
            removeLeaseCompImage({
              leaseCompId: ownerId,
              mediaId: tempMediaId,
            }),
          );
          console.error("Error uploading file");
        });
    });
  };

  const handleReorder = async (
    _reorderedFiles: typeof mediaFiles,
    sourceIndex: number,
    destinationIndex: number,
  ) => {
    if (sourceIndex === destinationIndex) return;
    const reorderedList = Array.from(mediaFiles);
    const [movedItem] = reorderedList.splice(sourceIndex, 1);
    reorderedList.splice(destinationIndex, 0, movedItem);

    dispatch(
      reorderLeaseCompImages({
        leaseCompId: ownerId,
        sourceIndex,
        destinationIndex,
      }),
    );

    if (currentSurvey?.id === undefined) return;

    const updatedMedia = {
      surveyId: currentSurvey.id,
      propertyId: ownerId,
      leaseCompId: ownerId,
      mediaType: "IMAGES",
      mediaId: movedItem.mediaId,
      order: destinationIndex.toString(),
      ownerType: "LEASE_COMP" as const,
    };

    reorderLeaseCompImageAPI(updatedMedia)
      .unwrap()
      .catch((_error) => {
        console.error("Error reordering lease comp images");
      });
  };

  const sortableItems = mediaFiles.map((file) => ({
    ...file,
    id: file.mediaId,
  }));

  const { DndContextProps, SortableContextProps, orderedItems } =
    useSortableList({
      items: sortableItems,
      onReorder: handleReorder,
      strategy: "horizontal",
    });

  return (
    <>
      <div className="flex items-start gap-x-4">
        <EmeraldFileUpload
          className="min-w-[440px]! [&_.file-upload-browse]:whitespace-nowrap"
          acceptedFileTypes={validImageTypes}
          allowDragAndDrop={true}
          allowMultiple={true}
          uploadButtonLabel="Browse file"
          maxFileSize={52428800}
          inputLabel="Select file(s) or drop here to upload"
          inputLabelCaption={`Upto 50 MB file size and only ${acceptedKeyTypes} formats`}
          variant="default"
          onChange={(event, files) => handleFileUpload(event, files)}
          onDrop={(event, files) => handleFileUpload(event, files)}
        />

        {errorBool && (
          <div className="attachment-error">
            <EmeraldNotification
              variant="error"
              title="The following file/s failed"
              message={failedFiles
                .map((f) => `• File: ${f.fileName}  Reason: ${f.errorMsg}`)
                .join("\n")}
              closable
              onClose={() => {
                setError(false);
                setFailedFiles([]);
              }}
              style={{ whiteSpace: "pre-line" }}
            />
          </div>
        )}
      </div>

      <DndContext {...DndContextProps}>
        <SortableContext {...SortableContextProps}>
          <div className="flex flex-wrap gap-6">
            {orderedItems.map((file) => (
              <LeaseCompPhotoThumbnail
                key={file.mediaId}
                eachFile={file}
                ownerId={ownerId}
                surveyId={currentSurvey?.id || ""}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </>
  );
};

export default LeaseCompPhotoUpload;
